<template>
  <nav class="navbar" role="navigation" aria-label="Main navigation">
    <a class="skip-link" href="#main-content">Saltar al contenido</a>

    <div class="navbar-container">
      <div class="navbar-brand">
        <router-link :to="{ name: 'home' }" class="brand-link" @click="closeMenu">
          {{ siteName }}
        </router-link>
      </div>

      <!-- Desktop Menu -->
      <div class="desktop-menu">
        <router-link
          v-for="item in menuItems"
          :key="item.routeName"
          :to="{ name: item.routeName }"
          class="nav-link"
          active-class="active"
        >
          {{ item.title }}
        </router-link>
      </div>

      <!-- Mobile Toggle -->
      <button
        class="navbar-toggle"
        :aria-expanded="isOpen"
        aria-label="Toggle menu"
        @click="toggleMenu"
      >
        <span class="hamburger-box">
          <span class="hamburger-inner" :class="{ 'is-active': isOpen }"></span>
        </span>
      </button>
    </div>

    <!-- Mobile Menu Drawer -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="isOpen" class="mobile-backdrop" @click="closeMenu"></div>
      </Transition>

      <Transition name="slide">
        <div v-if="isOpen" class="mobile-drawer" role="dialog" aria-modal="true">
          <div class="drawer-header">
            <span class="drawer-title">Menu</span>
            <button class="close-btn" aria-label="Close menu" @click="closeMenu">&times;</button>
          </div>
          <nav class="drawer-nav">
            <router-link
              v-for="item in menuItems"
              :key="item.routeName"
              :to="{ name: item.routeName }"
              class="drawer-link"
              active-class="active"
              @click="closeMenu"
            >
              {{ item.title }}
            </router-link>
          </nav>
        </div>
      </Transition>
    </Teleport>
  </nav>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const isOpen = ref(false);

const menuItems = [
  // { title: 'Canchas', routeName: 'fields-list' },
  // { title: 'Mapa', routeName: 'map' },
  // { title: 'Sign in', routeName: 'login' },
  { title: 'About', routeName: 'about' },
];

const toggleMenu = () => (isOpen.value = !isOpen.value);
const closeMenu = () => (isOpen.value = false);
const siteName = ref('Terapias de Bienestar');
// Close menu on route change
watch(() => route.path, closeMenu);

// Lock body scroll when menu is open
watch(isOpen, (val) => {
  if (val) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isOpen.value) closeMenu();
};

onMounted(() => window.addEventListener('keydown', handleKeydown));
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = ''; // Ensure cleanup
});
</script>

<style scoped>
/* Variables */
:root {
  --navbar-height: 60px;
  --primary-color: #f97316;
  --text-color: #8b067a;
  --bg-color: #c9c1c1;
}

.navbar {
  background: var(--bg-color);
  height: 60px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 0 1rem;
}

.navbar-container {
  max-width: 99%;
  margin: 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand-link {
  font-size: 1.5rem;
  font-weight: 800;
  color: #8d17c4;
  text-decoration: none;
  letter-spacing: -0.025em;
}

/* Desktop Menu */
.desktop-menu {
  display: none;
  gap: 2rem;
}

.nav-link {
  color: #4b5563;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.nav-link:hover,
.nav-link.active {
  color: #f97316;
}

/* Mobile Toggle */
.navbar-toggle {
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hamburger-box {
  width: 24px;
  height: 24px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hamburger-inner {
  width: 24px;
  height: 2px;
  background-color: #111827;
  transition: all 0.2s ease;
  position: relative;
}

.hamburger-inner::before,
.hamburger-inner::after {
  content: '';
  position: absolute;
  width: 24px;
  height: 2px;
  background-color: #111827;
  left: 0;
  transition: all 0.2s ease;
}

.hamburger-inner::before {
  top: -8px;
}
.hamburger-inner::after {
  top: 8px;
}

/* Mobile Drawer & Backdrop */
.mobile-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  backdrop-filter: blur(2px);
}

.mobile-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 280px;
  background: #ffffff;
  z-index: 9999;
  box-shadow: -4px 0 15px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.drawer-header {
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f3f4f6;
}

.drawer-title {
  font-weight: 600;
  font-size: 1.1rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  line-height: 1;
}

.drawer-nav {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.drawer-link {
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: #374151;
  font-weight: 500;
  border-radius: 0.5rem;
  transition: background 0.2s;
}

.drawer-link:hover,
.drawer-link.active {
  background: #fff7ed;
  color: #f97316;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* Responsive */
@media (min-width: 768px) {
  .desktop-menu {
    display: flex;
  }
  .navbar-toggle {
    display: none;
  }
}

.skip-link {
  position: absolute;
  left: -9999px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
.skip-link:focus {
  position: static;
  width: auto;
  height: auto;
}
</style>
